
import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button } from '@/components/ui/button';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { PasswordStrengthMeter } from '@/components/PasswordStrengthMeter';
import { validatePassword } from '@/utils/password-validation';
import { checkEmailAvailability } from '@/utils/emailValidation';
import { Loader2 } from 'lucide-react';

// Define schema for account registration with enhanced password validation
const accountSchema = z.object({
  fullName: z.string().min(2, "Full name is required"),
  email: z.string().email("Invalid email address"),
  password: z.string()
    .min(8, "Password must be at least 8 characters")
    .refine(
      (password) => /[A-Z]/.test(password), 
      { message: "Password must contain at least one uppercase letter" }
    )
    .refine(
      (password) => /[a-z]/.test(password),
      { message: "Password must contain at least one lowercase letter" }
    )
    .refine(
      (password) => /\d/.test(password),
      { message: "Password must contain at least one number" }
    )
    .refine(
      (password) => /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password),
      { message: "Password must contain at least one special character" }
    ),
  acceptTerms: z.boolean().refine((val) => val === true, {
    message: "You must accept the terms and conditions",
  }),
});

export type AccountFormData = z.infer<typeof accountSchema>;

interface AccountRegistrationFormProps {
  onSubmit: (data: AccountFormData) => void;
  onCancel: () => void;
  onSignInClick: () => void;
}

export function AccountRegistrationForm({ 
  onSubmit, 
  onCancel,
  onSignInClick
}: AccountRegistrationFormProps) {
  const [passwordStrength, setPasswordStrength] = useState(0);
  const [passwordFeedback, setPasswordFeedback] = useState<string[]>([]);
  const [emailValidating, setEmailValidating] = useState(false);
  const [emailAvailable, setEmailAvailable] = useState(false);
  const [emailError, setEmailError] = useState<string | null>(null);

  // Create a form instance completely independent from any other form
  const form = useForm<AccountFormData>({
    resolver: zodResolver(accountSchema),
    defaultValues: {
      fullName: "",
      email: "",
      password: "",
      acceptTerms: false,
    },
  });

  // Handle password input to update strength meter
  const handlePasswordChange = (value: string) => {
    const { strength, feedback } = validatePassword(value);
    setPasswordStrength(strength);
    setPasswordFeedback(feedback);
    return value;
  };

  // Debounced email validation
  useEffect(() => {
    const email = form.watch('email');
    
    if (!email || !email.includes('@')) {
      setEmailAvailable(false);
      setEmailError(null);
      return;
    }

    const timeoutId = setTimeout(async () => {
      setEmailValidating(true);
      setEmailError(null);
      
      try {
        const result = await checkEmailAvailability(email);
        setEmailAvailable(result.available);
        setEmailError(result.reason || null);
      } catch (error) {
        console.error('Email validation error:', error);
        // Fail closed - don't allow registration if validation fails
        setEmailAvailable(false);
        setEmailError('Unable to verify email availability. Please try again.');
      } finally {
        setEmailValidating(false);
      }
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [form.watch('email')]);

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
        <FormField
          control={form.control}
          name="fullName"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Full Name <span className="text-destructive">*</span></FormLabel>
              <FormControl>
                <Input {...field} autoComplete="name" />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        
        <FormField
          control={form.control}
          name="email"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Email <span className="text-destructive">*</span></FormLabel>
              <FormControl>
                <div className="relative">
                  <Input {...field} type="email" autoComplete="email" />
                  {emailValidating && (
                    <Loader2 className="absolute right-3 top-2.5 h-4 w-4 animate-spin text-muted-foreground" />
                  )}
                </div>
              </FormControl>
              {emailError && (
                <p className="text-sm font-medium text-destructive">{emailError}</p>
              )}
              <FormMessage />
            </FormItem>
          )}
        />
        
        <FormField
          control={form.control}
          name="password"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Password <span className="text-destructive">*</span></FormLabel>
              <FormControl>
                <Input 
                  {...field} 
                  type="password" 
                  autoComplete="new-password" 
                  onChange={(e) => {
                    const value = e.target.value;
                    field.onChange(value);
                    handlePasswordChange(value);
                  }}
                />
              </FormControl>
              <PasswordStrengthMeter 
                strength={passwordStrength} 
                feedback={passwordFeedback} 
              />
              <FormMessage />
            </FormItem>
          )}
        />
        
        <FormField
          control={form.control}
          name="acceptTerms"
          render={({ field }) => (
            <FormItem className="flex flex-row items-start space-x-3 space-y-0">
              <FormControl>
                <Checkbox
                  checked={field.value}
                  onCheckedChange={field.onChange}
                />
              </FormControl>
              <div className="space-y-1 leading-none">
                <FormLabel className="text-sm font-normal">
                  I accept the{" "}
                  <a
                    href="/terms-and-conditions"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary hover:underline"
                  >
                    Terms & Conditions
                  </a>
                  {" "}and{" "}
                  <a
                    href="/privacy-policy"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary hover:underline"
                  >
                    Privacy Policy
                  </a>
                </FormLabel>
                <FormMessage />
              </div>
            </FormItem>
          )}
        />
        
        <Button 
          type="submit" 
          className="w-full"
          disabled={passwordStrength < 3 || !emailAvailable || emailValidating}
        >
          {emailValidating ? 'Checking email...' : 'Continue'}
        </Button>
        
        <div className="text-center mt-4">
          <button
            type="button"
            onClick={onSignInClick}
            className="text-sm text-primary hover:underline"
          >
            Already have an account? Sign in
          </button>
        </div>
      </form>
    </Form>
  );
}
